export default class VacancyDraft {

    constructor(data = {}) {
  
      // ========================================================
      // BASE
      // ========================================================
  
      this.id =
        data.id ??
        null;
  
      this.source =
        data.source ??
        {};
  
      this.vacancy =
        data.vacancy ??
        {};
  
      this.classification =
        data.classification ??
        {};
  
      this.rawPost =
        data.raw_post ??
        {};
  
      // ========================================================
      // RESOLUTION
      // ========================================================
  
      this.companyResolution =
        data.company_resolution ??
        {};
  
      this.imageResolution =
        data.image_resolution ??
        {};
  
      // ========================================================
      // PROCESSING
      // ========================================================
  
      this.processingStatus =
        data.processing_status ??
        "pending";
  
      this.processingError =
        data.processing_error ??
        null;
  
      this.isProcessed =
        data.is_processed === true;
  
      // ========================================================
      // BACKEND COMPUTED FIELDS
      // ========================================================
  
      this.companyResolutionStatus =
        data.company_resolution_status ??
        this.companyResolution?.status ??
        "pending";
  
      this.imageResolutionStatus =
        data.image_resolution_status ??
        this.imageResolution?.status ??
        "pending";
  
      this.isReady =
        data.is_ready === true;
  
      // ========================================================
      // RESULT
      // ========================================================
  
      this.publishedVacancyId =
        data.published_vacancy_id ??
        null;
  
      this.resolvedCompanyId =
        data.resolved_company_id ??
        null;
  
      // ========================================================
      // DATES
      // ========================================================
  
      this.createdAt =
        data.created_at ??
        null;
  
      this.updatedAt =
        data.updated_at ??
        null;
  
      this.lastSeenAt =
        data.last_seen_at ??
        null;
  
      this.processedAt =
        data.processed_at ??
        null;
  
      // ========================================================
      // RAW
      // ========================================================
  
      this.raw =
        data;
    }
  
    // ==========================================================
    // VACANCY
    // ==========================================================
  
    get title() {
  
      return (
        this.vacancy?.position?.title ??
        this.rawPost?.metadata?.title ??
        null
      );
    }
  
    get level() {
  
      return (
        this.vacancy?.position?.level ??
        null
      );
    }
  
    get summary() {
  
      return (
        this.vacancy?.summary ??
        null
      );
    }
  
    // ==========================================================
    // COMPANY
    // ==========================================================
  
    get companyName() {
  
      return (
        this.companyResolution?.company_name ??
        this.vacancy?.company?.name ??
        this.rawPost?.metadata?.company ??
        null
      );
    }
  
    get companyId() {
  
      return (
        this.companyResolution?.company_id ??
        this.resolvedCompanyId ??
        null
      );
    }
  
    get companyMatchType() {
  
      return (
        this.companyResolution?.match_type ??
        null
      );
    }
  
    get companyMatchScore() {
  
      return (
        this.companyResolution?.score ??
        null
      );
    }
  
    get companyResolutionReason() {
  
      return (
        this.companyResolution?.reason ??
        null
      );
    }
  
    get wasCompanyResolvedByAI() {
  
      return (
        this.companyResolution?.ai_used === true
      );
    }
  
    // ==========================================================
    // IMAGE
    // ==========================================================
  
    get imageUrl() {
  
      return (
        this.vacancy?.image_url ??
        this.imageResolution?.url ??
        null
      );
    }
  
    get imageStoragePath() {
  
      return (
        this.imageResolution?.storage_path ??
        null
      );
    }
  
    // ==========================================================
    // SOURCE
    // ==========================================================
  
    get sourceProvider() {
  
      return (
        this.source?.provider ??
        this.vacancy?.source?.provider ??
        this.rawPost?.source ??
        null
      );
    }
  
    get sourceUrl() {
  
      return (
        this.source?.url ??
        this.vacancy?.source?.url ??
        this.rawPost?.source_url ??
        null
      );
    }
  
    get externalId() {
  
      return (
        this.source?.external_id ??
        this.vacancy?.source?.external_id ??
        this.rawPost?.external_id ??
        null
      );
    }
  
    // ==========================================================
    // LOCATION
    // ==========================================================
  
    get city() {
  
      return (
        this.vacancy?.location?.city ??
        this.rawPost?.metadata?.city ??
        this.rawPost?.metadata?.location ??
        null
      );
    }
  
    get country() {
  
      return (
        this.vacancy?.location?.country ??
        null
      );
    }
  
    get isRemote() {
  
      return (
        this.vacancy?.location?.remote ??
        null
      );
    }
  
    get isHybrid() {
  
      return (
        this.vacancy?.location?.hybrid ??
        null
      );
    }
  
    // ==========================================================
    // EMPLOYMENT
    // ==========================================================
  
    get employmentType() {
  
      return (
        this.vacancy?.employment?.type ??
        null
      );
    }
  
    // ==========================================================
    // SALARY
    // ==========================================================
  
    get salary() {
  
      return (
        this.vacancy?.salary ??
        {}
      );
    }
  
    get salaryFrom() {
  
      return (
        this.salary?.from ??
        null
      );
    }
  
    get salaryTo() {
  
      return (
        this.salary?.to ??
        null
      );
    }
  
    get salaryCurrency() {
  
      return (
        this.salary?.currency ??
        null
      );
    }
  
    get salaryText() {
  
      return (
        this.salary?.text ??
        null
      );
    }
  
    // ==========================================================
    // CONTACTS
    // ==========================================================
  
    get contacts() {
  
      return (
        this.vacancy?.contacts ??
        {}
      );
    }
  
    get email() {
  
      return (
        this.contacts?.email ??
        null
      );
    }
  
    get telegram() {
  
      return (
        this.contacts?.telegram ??
        null
      );
    }
  
    get applyUrl() {
  
      return (
        this.contacts?.apply_url ??
        null
      );
    }
  
    // ==========================================================
    // CONTENT
    // ==========================================================
  
    get responsibilities() {
  
      return Array.isArray(
        this.vacancy?.responsibilities
      )
        ? this.vacancy.responsibilities
        : [];
    }
  
    get requirements() {
  
      return Array.isArray(
        this.vacancy?.requirements
      )
        ? this.vacancy.requirements
        : [];
    }
  
    get conditions() {
  
      return Array.isArray(
        this.vacancy?.conditions
      )
        ? this.vacancy.conditions
        : [];
    }
  
    get skills() {
  
      return Array.isArray(
        this.vacancy?.skills
      )
        ? this.vacancy.skills
        : [];
    }
  
    // ==========================================================
    // CLASSIFICATION
    // ==========================================================
  
    get classificationType() {
  
      return (
        this.classification?.type ??
        null
      );
    }
  
    get classificationConfidence() {
  
      return (
        this.classification?.confidence ??
        null
      );
    }
  
    get classificationReason() {
  
      return (
        this.classification?.reason ??
        null
      );
    }
  
    // ==========================================================
    // STATUS HELPERS
    // ==========================================================
  
    get isCompanyResolved() {
  
      return (
        this.companyResolutionStatus
        === "resolved"
      );
    }
  
    get isCompanyNotFound() {
  
      return (
        this.companyResolutionStatus
        === "not_found"
      );
    }
  
    get isCompanyAmbiguous() {
  
      return (
        this.companyResolutionStatus
        === "ambiguous"
      );
    }
  
    get isCompanyFailed() {
  
      return (
        this.companyResolutionStatus
        === "failed"
      );
    }
  
    get isImageResolved() {
  
      return (
        this.imageResolutionStatus
        === "resolved"
      );
    }
  
    get isImageNotFound() {
  
      return (
        this.imageResolutionStatus
        === "not_found"
      );
    }
  
    get isImageFailed() {
  
      return (
        this.imageResolutionStatus
        === "failed"
      );
    }
  
    // ==========================================================
    // DATE HELPERS
    // ==========================================================
  
    get createdDate() {
  
      return (
        this._parseDate(
          this.createdAt
        )
      );
    }
  
    get updatedDate() {
  
      return (
        this._parseDate(
          this.updatedAt
        )
      );
    }
  
    // ==========================================================
    // STATIC
    // ==========================================================
  
    static fromJSON(data) {
  
      return new VacancyDraft(
        data
      );
    }
  
    static fromArray(items) {
  
      if (!Array.isArray(items)) {
        return [];
      }
  
      return items.map(
        item =>
          new VacancyDraft(
            item
          )
      );
    }
  
    // ==========================================================
    // INTERNAL
    // ==========================================================
  
    _parseDate(value) {
  
      if (!value) {
        return null;
      }
  
      const date =
        new Date(
          value
        );
  
      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
  
        return null;
      }
  
      return date;
    }
  }